/**
 * The one way either tree talks to the ondit API: the panel's `request()` and
 * the Agent's contract client both go through `send`, and every refusal comes
 * back as an `ApiError`.
 *
 * There were two, and the Agent's read `message` and `error` off an error body
 * — fields the Rust API never sends — so every refusal on an Agent screen read
 * "Request failed with 400" while the same refusal on a panel screen read the
 * sentence the server's `message_key` names.
 */

import { CLIENT_ID_HEADER, getClientId } from "@/lib/api/client-id";
import { apiFetch } from "~/core/api/url";
import { type Locale, lookup, readLocale } from "~/core/i18n";

/**
 * The refusals a screen cannot answer on its own.
 *
 * Out of credits wants the top-up dialog; a lapsed plan wants the renewal
 * screen. Every call site already shows the error's sentence, but the thing
 * to do about it is the same on every screen, so it is announced once here
 * and the shell opens the right dialog over whatever the person was doing.
 */
export const GATE_EVENT = "ondit:gated";
export type GateReason = "credits" | "subscription_expired" | "subscription_required";

export function announceGate(reason: string): void {
	if (reason !== "credits" && reason !== "subscription_expired" && reason !== "subscription_required") return;
	if (typeof window === "undefined") return;
	window.dispatchEvent(new CustomEvent<GateReason>(GATE_EVENT, { detail: reason }));
}

type Params = Record<string, string | number>;

/**
 * The sentence for a refusal: its own key, then the generic one for its code,
 * then the generic one for everything. Never the key itself — a key on screen
 * is a string somebody forgot, and the reader cannot do anything with it.
 */
export function sentence(locale: Locale, messageKey: string, code = "internal", params?: Params | null): string {
	const vars = params ?? undefined;
	return (
		lookup(locale, messageKey, vars) ??
		lookup(locale, `errors.${code}`, vars) ??
		lookup(locale, "errors.internal") ??
		messageKey
	);
}

export class ApiError extends Error {
	constructor(
		readonly status: number,
		readonly code: string,
		readonly messageKey: string,
		readonly detail?: string | null,
		/** The values a refusal's sentence fills in: a limit, a count, a name. */
		readonly params?: Params | null,
	) {
		// The sentence rather than the key, because most of the Agent's screens
		// show `error.message` as it is — through `getErrorMessage` or straight
		// into a toast — and cannot be taught one by one to translate.
		super(sentence(readLocale(), messageKey, code, params));
		this.name = "ApiError";
		if (code === "gated" && detail) announceGate(detail);
	}

	/** The gate reason, when this is a refusal to spend. */
	get gate(): string | null {
		return this.code === "gated" ? (this.detail ?? null) : null;
	}

	/** The sentence in a given language, for a screen that re-renders when the reader switches. */
	localized(locale: Locale): string {
		return sentence(locale, this.messageKey, this.code, this.params);
	}

	/**
	 * The refusal as prose: the server's detail when it is a sentence, the
	 * translation otherwise.
	 *
	 * A screen that wants the detail wants "Coda and Notion share only 2
	 * datapoints" — the part no translation can carry. But `public_detail` on a
	 * conflict is the same machine code `messageKey` already names, so reading
	 * `detail ?? localized()` put `gap_already_generated` in a toast.
	 */
	reason(locale: Locale): string {
		return this.detail && !CODE_SHAPE.test(this.detail) ? this.detail : this.localized(locale);
	}

	get isUnauthorized() {
		return this.status === 401;
	}
}

/** What an error body says, whichever shape it came in. */
interface ErrorBody {
	code: string;
	messageKey: string;
	detail: string | null;
	params: Params | null;
}

const CODE_SHAPE = /^[a-z][a-z0-9_]*$/;

/**
 * `{code, message_key, detail?, params?}` is what `server/src/error.rs` sends.
 * A few Agent routes answer `{error: code}` instead — the hosted chat's lock
 * screen is read by a visitor's browser and names its form that way — so a
 * code-shaped `error` stands in for a missing `code`. Prose in `error` is not
 * a code and is not read.
 */
export function readErrorBody(body: unknown): ErrorBody {
	const record = body && typeof body === "object" ? (body as Record<string, unknown>) : {};
	const text = (value: unknown) => (typeof value === "string" && value.length > 0 ? value : undefined);
	const fallback = text(record.error);
	const code = text(record.code) ?? (fallback && CODE_SHAPE.test(fallback) ? fallback : "internal");
	const params = record.params && typeof record.params === "object" ? (record.params as Params) : null;
	return {
		code,
		messageKey: text(record.message_key) ?? `errors.${code}`,
		detail: text(record.detail) ?? null,
		params,
	};
}

/** Builds the error a failed response throws; the Agent's client builds its subclass. */
export type Failure = (status: number, body: unknown, raw: string | undefined) => Error;

const apiError: Failure = (status, body) => {
	const { code, messageKey, detail, params } = readErrorBody(body);
	return new ApiError(status, code, messageKey, detail, params);
};

/**
 * Fetch `url`, and throw what `fail` builds from the body when the answer is
 * not a 2xx. Returns the response unread otherwise, because what a success
 * means — JSON, a stream, a download — is the caller's business.
 */
export async function send(url: string, init: RequestInit = {}, fail: Failure = apiError): Promise<Response> {
	const headers = new Headers(init.headers);
	if (typeof init.body === "string" && !headers.has("content-type")) headers.set("content-type", "application/json");
	// Relative Agent calls carry the tab id even when production routes them to the API host.
	const clientId = url.startsWith("/") && !url.startsWith("//") ? getClientId() : undefined;
	if (clientId && !headers.has(CLIENT_ID_HEADER)) headers.set(CLIENT_ID_HEADER, clientId);

	// The session is a cookie, so every call has to carry it.
	const response = await apiFetch(url, { ...init, headers, credentials: "include" });
	if (response.ok) return response;

	const raw = await response.text().catch(() => "");
	let body: unknown;
	try {
		body = raw ? JSON.parse(raw) : undefined;
	} catch {
		body = raw;
	}
	throw fail(response.status, body, raw || undefined);
}
